import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Users, CalendarDays, Plus, Trash2, ShieldCheck, Database, Download, Upload, KeyRound, UserPlus, Pencil } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground mb-3">
      <Icon size={16} strokeWidth={2} />
      <span className="text-xs tracking-[0.15em] uppercase font-medium">{label}</span>
    </div>
    <div className="font-heading font-black text-4xl tracking-tight mono">{value}</div>
  </div>
);

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [adding, setAdding] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInput = useRef();

  // Create user form
  const [uName, setUName] = useState("");
  const [uUsername, setUUsername] = useState("");
  const [uPassword, setUPassword] = useState("");
  const [uDept, setUDept] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);

  // Edit user dialog
  const [editTarget, setEditTarget] = useState(null);
  const [euName, setEuName] = useState("");
  const [euDept, setEuDept] = useState("");
  const [savingUser, setSavingUser] = useState(false);

  const load = async () => {
    const [s, d, u] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/departments"),
      api.get("/admin/users"),
    ]);
    setStats(s.data);
    setDepts(d.data.departments);
    setUsers(u.data.users);
  };

  useEffect(() => {
    if (user === null) return; // still checking auth
    if (user.role !== "admin") {
      navigate("/");
      return;
    }
    load().catch(() => toast.error("Gagal memuat panel admin"));
    // eslint-disable-next-line
  }, [user]);

  const addDept = async (e) => {
    e.preventDefault();
    if (!newDept.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/departments", { name: newDept.trim() });
      setNewDept("");
      toast.success("Departemen ditambahkan");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const removeDept = async (id) => {
    try {
      await api.delete(`/admin/departments/${id}`);
      toast.success("Departemen dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!uName.trim() || !uUsername.trim() || !uPassword || !uDept) {
      toast.error("Lengkapi nama, username, password, dan departemen");
      return;
    }
    setCreatingUser(true);
    try {
      await api.post("/admin/users", {
        name: uName.trim(),
        username: uUsername.trim(),
        password: uPassword,
        department: uDept,
      });
      toast.success("Akun departemen dibuat");
      setUName(""); setUUsername(""); setUPassword(""); setUDept("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingUser(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetting(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { password: resetPw });
      toast.success(`Password ${resetTarget.name} direset`);
      setResetTarget(null);
      setResetPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const openEditUser = (u) => {
    setEditTarget(u);
    setEuName(u.name || "");
    setEuDept(u.department || "");
  };

  const submitEditUser = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    if (!euDept) {
      toast.error("Pilih departemen");
      return;
    }
    setSavingUser(true);
    try {
      await api.put(`/admin/users/${editTarget.id}`, { name: euName, department: euDept });
      toast.success("Akun diperbarui");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUser(false);
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `falcom-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup berhasil diunduh");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal membuat backup");
    } finally {
      setBackingUp(false);
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/restore", fd);
      const counts = Object.entries(data.restored || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Restore berhasil (${counts})`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal restore");
    } finally {
      setRestoring(false);
      if (restoreInput.current) restoreInput.current.value = "";
    }
  };

  return (
    <Layout>
      <div className="p-8 lg:p-12 max-w-[1400px]">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary font-medium mb-2 mono">
            <ShieldCheck size={14} /> Super Admin
          </div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight">Control Panel</h1>
          <p className="text-muted-foreground mt-2">Kelola departemen, akun pengguna, dan ringkasan sistem.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={CalendarDays} label="Total Rapat" value={stats?.meetings ?? "—"} testid="stat-meetings" />
          <StatCard icon={Building2} label="Departemen" value={stats?.departments ?? "—"} testid="stat-departments" />
          <StatCard icon={Users} label="Pengguna" value={stats?.users ?? "—"} testid="stat-users" />
        </div>

        {/* Account management */}
        <div className="border border-border bg-card p-6 mb-6" data-testid="user-management-section">
          <div className="flex items-center gap-2 mb-5">
            <UserPlus size={16} className="text-primary" />
            <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">Akun Pengguna per Bagian</h3>
          </div>

          <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6 pb-6 border-b border-border">
            <div className="space-y-1.5">
              <Label className="text-xs">Nama</Label>
              <Input
                data-testid="user-name-input"
                value={uName}
                onChange={(e) => setUName(e.target.value)}
                placeholder="Nama lengkap"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input
                data-testid="user-username-input"
                value={uUsername}
                onChange={(e) => setUUsername(e.target.value)}
                placeholder="username"
                autoCapitalize="none"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password</Label>
              <Input
                data-testid="user-password-input"
                type="text"
                value={uPassword}
                onChange={(e) => setUPassword(e.target.value)}
                placeholder="min. 6 karakter"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Departemen</Label>
              <Select value={uDept} onValueChange={setUDept}>
                <SelectTrigger data-testid="user-department-select" className="rounded-none">
                  <SelectValue placeholder="Pilih bagian" />
                </SelectTrigger>
                <SelectContent>
                  {depts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={creatingUser}
                data-testid="create-user-btn"
                className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Plus size={16} className="mr-2" /> Buat Akun
              </Button>
            </div>
          </form>

          <div className="divide-y divide-border" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 py-3" data-testid={`admin-user-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{u.name}</span>
                    <Badge
                      variant={u.role === "admin" ? "default" : "outline"}
                      className={`rounded-none text-[10px] ${u.role === "admin" ? "bg-primary text-primary-foreground" : ""}`}
                    >
                      {u.role === "admin" ? "Super Admin" : (u.department || "—")}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mono">
                    {u.username ? `@${u.username}` : (u.email || "—")}
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 mono" data-testid={`user-last-login-${u.id}`}>
                    Login terakhir: {u.last_login ? new Date(u.last_login).toLocaleString("id-ID") : "Belum pernah"}
                  </div>
                </div>
                {u.role !== "admin" && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEditUser(u)}
                      data-testid={`edit-user-${u.id}`}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                      title="Ubah nama / departemen"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => { setResetTarget(u); setResetPw(""); }}
                      data-testid={`reset-pw-${u.id}`}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                      title="Reset Password"
                    >
                      <KeyRound size={16} />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-user-${u.id}`}
                          className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                          title="Hapus akun"
                        >
                          <Trash2 size={16} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-heading tracking-tight">Hapus akun?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Akun "{u.name}" ({u.username}) akan dihapus dan tidak bisa login lagi.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none">Batal</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteUser(u.id)}
                            data-testid={`confirm-delete-user-${u.id}`}
                            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                          >
                            Hapus
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            ))}
            {users.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada pengguna.</p>}
          </div>
        </div>

        {/* Departments manager */}
        <div className="border border-border bg-card p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <Building2 size={16} className="text-primary" />
            <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">Kelola Departemen</h3>
          </div>

          <form onSubmit={addDept} className="flex gap-2 mb-5 pb-5 border-b border-border">
            <Input
              data-testid="new-department-input"
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              placeholder="Nama departemen baru…"
              className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <Button
              type="submit"
              disabled={adding}
              data-testid="add-department-btn"
              className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
            >
              <Plus size={16} className="mr-2" /> Tambah
            </Button>
          </form>

          <div className="divide-y divide-border" data-testid="admin-departments-list">
            {depts.map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-3" data-testid={`admin-dept-${d.id}`}>
                <span className="flex-1 text-sm font-medium">{d.name}</span>
                <Badge variant="outline" className="rounded-none mono text-[10px]">
                  {d.meeting_count} rapat
                </Badge>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`delete-dept-${d.id}`}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                      title="Hapus"
                    >
                      <Trash2 size={16} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-heading tracking-tight">Hapus departemen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Departemen "{d.name}" akan dihapus dari daftar pilihan.
                        {d.meeting_count > 0 && ` Rapat yang sudah memakai departemen ini (${d.meeting_count}) tidak akan berubah.`}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none">Batal</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => removeDept(d.id)}
                        data-testid={`confirm-delete-dept-${d.id}`}
                        className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        Hapus
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
            {depts.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada departemen.</p>}
          </div>
        </div>

        {/* Backup & Restore */}
        <div className="border border-border bg-card p-6" data-testid="backup-section">
          <div className="flex items-center gap-2 mb-2">
            <Database size={16} className="text-primary" />
            <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">Backup Database</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Unduh cadangan seluruh data (rapat, pengguna, departemen) sebagai satu file JSON, atau pulihkan dari file backup.
            <span className="block mt-1 text-xs">Catatan: file rekaman audio & notulen tidak termasuk dalam backup JSON ini.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={downloadBackup}
              disabled={backingUp}
              data-testid="download-backup-btn"
              className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Download size={16} className="mr-2" /> {backingUp ? "Menyiapkan…" : "Unduh Backup"}
            </Button>

            <input
              ref={restoreInput}
              type="file"
              accept=".json,application/json"
              onChange={restoreBackup}
              className="hidden"
              data-testid="restore-file-input"
            />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={restoring}
                  data-testid="restore-backup-btn"
                  className="rounded-none border-foreground/20"
                >
                  <Upload size={16} className="mr-2" /> {restoring ? "Memulihkan…" : "Restore dari Backup"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading tracking-tight">Pulihkan database?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Restore akan <span className="font-semibold text-primary">MENGGANTI</span> seluruh data rapat,
                    pengguna, dan departemen saat ini dengan isi file backup. Tindakan ini tidak dapat dibatalkan.
                    Sebaiknya unduh backup terlebih dahulu.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none">Batal</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => restoreInput.current?.click()}
                    data-testid="confirm-restore-btn"
                    className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    Pilih File & Restore
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="rounded-none" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              Ubah Akun{editTarget ? ` — @${editTarget.username}` : ""}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEditUser} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="euname">Nama</Label>
              <Input
                id="euname"
                data-testid="edit-user-name-input"
                value={euName}
                onChange={(e) => setEuName(e.target.value)}
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Departemen</Label>
              <Select value={euDept} onValueChange={setEuDept}>
                <SelectTrigger data-testid="edit-user-department-select" className="rounded-none">
                  <SelectValue placeholder="Pilih departemen" />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([euDept, ...depts.map((d) => d.name)].filter(Boolean))].map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={savingUser}
                data-testid="save-user-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {savingUser ? "Menyimpan…" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="rounded-none" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              Reset Password{resetTarget ? ` — ${resetTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="resetpw">Password Baru (min. 6 karakter)</Label>
              <Input
                id="resetpw"
                type="text"
                data-testid="reset-password-input"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={resetting}
                data-testid="submit-reset-password-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {resetting ? "Menyimpan…" : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
